import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useLang } from "../i18n";
import PolandMap from "../components/PolandMap";
import { StatusBadge, DemoRibbon } from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarDays } from "lucide-react";

const STATUSES = ["adopted", "draft", "consultation", "pending", "unknown"];

export default function TrackerPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [gminas, setGminas] = useState([]);
  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [woj, setWoj] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    api.get("/gminas").then((r) => setGminas(r.data.results)).catch(() => {});
    api.get("/tracker/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/consultations").then((r) => setConsultations(r.data.results)).catch(() => {});
  }, []);

  const wojList = useMemo(() => [...new Set(gminas.map((g) => g.wojewodztwo))].sort(), [gminas]);
  const filtered = gminas.filter(
    (g) => (woj === "all" || g.wojewodztwo === woj) && (status === "all" || g.plan_ogolny_status === status)
  );
  const upcoming = consultations
    .filter((c) => c.status !== "closed")
    .sort((a, b) => a.end_date.localeCompare(b.end_date))
    .slice(0, 5);

  return (
    <div data-testid="tracker-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="flex items-center gap-3">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">{t("tracker.title")}</h1>
        <DemoRibbon label={t("demo.label")} />
      </div>
      <p className="mt-2 max-w-2xl text-base text-slate-600">{t("tracker.subtitle")}</p>

      {stats && (
        <div data-testid="tracker-stats-cards" className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <div className="rounded-xl border bg-slate-900 p-4 text-center text-white">
            <p className="font-heading text-3xl font-extrabold">{stats.total}</p>
            <p className="text-xs uppercase tracking-wide text-slate-400">{t("tracker.stats.total")}</p>
          </div>
          {STATUSES.map((s) => (
            <div key={s} className="rounded-xl border bg-white p-4 text-center">
              <p className="font-heading text-3xl font-extrabold">{stats.by_status[s] || 0}</p>
              <StatusBadge status={s} label={t(`status.${s}`)} testId={`tracker-stat-${s}`} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Select value={woj} onValueChange={setWoj}>
          <SelectTrigger data-testid="filter-wojewodztwo" className="w-56 bg-white">
            <SelectValue placeholder={t("tracker.filter.woj")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tracker.all")}</SelectItem>
            {wojList.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="w-56 bg-white">
            <SelectValue placeholder={t("tracker.filter.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tracker.all")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="rounded-xl border bg-white p-4 lg:col-span-3">
          <p className="mb-2 text-xs text-slate-400">{t("tracker.clickHint")}</p>
          <PolandMap gminas={filtered} />
        </div>

        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border bg-white">
            <div className="max-h-[300px] divide-y overflow-y-auto">
              {filtered.map((g) => (
                <button
                  key={g.id}
                  data-testid={`tracker-gmina-row-${g.id}`}
                  onClick={() => navigate(`/gmina/${g.id}`)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-50"
                >
                  <div>
                    <p className="text-sm font-semibold">{g.name}</p>
                    <p className="text-xs text-slate-500">woj. {g.wojewodztwo} · {g.type}</p>
                  </div>
                  <StatusBadge status={g.plan_ogolny_status} label={t(`status.${g.plan_ogolny_status}`)} />
                </button>
              ))}
            </div>
          </div>

          <div data-testid="tracker-deadlines" className="rounded-xl border bg-white p-4">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-slate-500">
              {t("tracker.deadlines")}
            </h3>
            <div className="mt-3 space-y-2">
              {upcoming.map((c) => (
                <div key={c.id} data-testid={`deadline-${c.id}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{c.gmina_name}</p>
                    <p className="flex items-center gap-1 text-xs text-slate-500">
                      <CalendarDays size={11} /> {t("cons.deadline")}: {c.end_date}
                    </p>
                  </div>
                  <StatusBadge status={c.status} label={t(`cons.${c.status}`)} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
